import React, { useState, useEffect, useCallback } from "react";
import { Redirect } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { useAuthz, ROLES } from "context/AuthzContext";
import { useCompany } from "context/CompanyContext";
import { getEmailConfig, saveEmailConfig, getAutoRRStatus, getEmailTemplates, saveEmailTemplate } from "services/tenantUserAdminService";
import { TC, FS, FW } from "../tokens/text";

// ── Shared text-field style (single-line MUI TextField only) ────────────────
const inputSx = {
  "& .MuiInputBase-root": {
    color: TC.primary,
    background: "#f4f8ff",
    borderRadius: "8px",
    fontSize: "0.83rem",
  },
  "& .MuiInputBase-input": { padding: "8.5px 14px" },
  "& .MuiInputLabel-root": { color: TC.secondary, fontSize: "0.8rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: TC.link },
  "& .MuiOutlinedInput-notchedOutline": { bordercolor: "#7a94b0" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { bordercolor: "#7a94b0" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0075ff" },
};

// ── Native textarea (avoids MUI multiline scrollbar quirks) ──────────────────
function NativeTextarea({ value, onChange, placeholder, rows = 3 }) {
  const [focused, setFocused] = React.useState(false);
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e)}
      placeholder={placeholder}
      rows={rows}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: "100%",
        boxSizing: "border-box",
        background: "#f4f8ff",
        border: `1px solid ${focused ? "#0075ff" : "rgba(255,255,255,0.12)"}`,
        borderRadius: "8px",
        color: TC.primary, fontSize: "0.83rem",
        lineHeight: 1.6,
        padding: "10px 14px",
        resize: "vertical",
        outline: "none",
        fontFamily: "inherit",
        overflowY: "auto",
        display: "block",
        transition: "border-color 0.15s",
        scrollbarWidth: "thin",
        scrollbarColor: "#c0d4ef transparent",
      }}
    />
  );
}

function SectionCard({ children, ...props }) {
  return (
    <VuiBox
      p={3}
      sx={{
        background: "#f4f8ff",
        border: "1px solid #d8e6f7",
        borderRadius: "14px",
        ...props.sx,
      }}
    >
      {children}
    </VuiBox>
  );
}

function SectionHeading({ children }) {
  return (
    <VuiTypography
      variant="caption"
      fontWeight="bold"
      sx={{ color: TC.secondary, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.68rem", opacity: 0.7, display: "block", mb: 2 }}
    >
      {children}
    </VuiTypography>
  );
}

function FieldRow({ label, hint, children }) {
  return (
    <VuiBox mb={2.5}>
      <VuiBox display="flex" alignItems="center" gap={0.5} mb={0.8}>
        <VuiTypography variant="caption" sx={{ color: TC.primary, fontSize: FS.sm, fontWeight: FW.medium }}>
          {label}
        </VuiTypography>
        {hint && (
          <Tooltip title={hint} placement="right">
            <InfoOutlinedIcon sx={{ fontSize: 14, color: TC.secondary, cursor: "help" }} />
          </Tooltip>
        )}
      </VuiBox>
      {children}
    </VuiBox>
  );
}

// ── Live email preview ───────────────────────────────────────────────────────
function interpolate(template, vals) {
  return (template || "")
    .replace(/\{contactName\}/g,  vals.contactName)
    .replace(/\{companyName\}/g,  vals.companyName)
    .replace(/\{period\}/g,       vals.period)
    .replace(/\{deadline\}/g,     vals.deadline);
}

const PREVIEW_VALS = {
  contactName: "Sarah Johnson",
  companyName: "Acme Retail Ltd",
  period: "31 March 2025",
  deadline: "31 December 2025",
};

function EmailPreview({ form }) {
  const firmName    = form.firmName    || "Your Firm Name";
  const tagline     = form.tagline     || "Trusted accountants & advisors";
  const replyTo     = form.replyToEmail|| "accounts@yourfirm.co.uk";
  const signOff     = form.signOffText || "Kind regards,";
  const footerText  = form.emailFooterText || "This email was sent on behalf of your accountant. Please do not reply directly — use the portal link above.";
  const opening     = form.openingParagraphMode === "custom"
    ? interpolate(form.customOpeningParagraph, PREVIEW_VALS)
    : `Dear ${PREVIEW_VALS.contactName},\n\nWe are writing to request the financial records needed to prepare the annual accounts for ${PREVIEW_VALS.companyName} for the year ended ${PREVIEW_VALS.period}. Please provide the documents listed below at your earliest convenience — the filing deadline is ${PREVIEW_VALS.deadline}.`;

  return (
    <VuiBox sx={{
      background: "#e8edf2",
      borderRadius: "14px",
      p: "20px 16px",
      border: "1px solid #d0daea",
    }}>
      <VuiBox sx={{
        background: "#ffffff",
        borderRadius: "10px 10px 0 0",
        borderBottom: "1px solid #e0e8f0",
        px: 2, py: 1,
        display: "flex", alignItems: "center", gap: 1,
      }}>
        <VuiBox sx={{ width: 10, height: 10, borderRadius: "50%", background: "#f44336", flexShrink: 0 }} />
        <VuiBox sx={{ width: 10, height: 10, borderRadius: "50%", background: "#ff9800", flexShrink: 0 }} />
        <VuiBox sx={{ width: 10, height: 10, borderRadius: "50%", background: "#4caf50", flexShrink: 0 }} />
        <VuiTypography sx={{ color: TC.tertiary, fontSize: "0.65rem", ml: 1, flex: 1 }}>
          📧 From: {replyTo} · To: {PREVIEW_VALS.contactName}
        </VuiTypography>
      </VuiBox>

      <VuiBox sx={{
        background: "#ffffff",
        borderRadius: "0 0 10px 10px",
        overflow: "hidden",
        fontFamily: "Arial, Helvetica, sans-serif",
      }}>
        <VuiBox sx={{
          background: "linear-gradient(135deg, #0044cc 0%, #0075ff 60%, #00a8ff 100%)",
          px: 3, py: 2.5,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <VuiBox>
            <VuiTypography sx={{ color: TC.white, fontWeight: 800, fontSize: "1.05rem", lineHeight: 1.2 }}>
              {firmName}
            </VuiTypography>
            {tagline && (
              <VuiTypography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.68rem", mt: 0.3 }}>
                {tagline}
              </VuiTypography>
            )}
          </VuiBox>
          <VuiBox sx={{
            background: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "8px", px: 1.5, py: 0.5,
          }}>
            <VuiTypography sx={{ color: TC.white, fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.06em" }}>
              ANNUAL ACCOUNTS
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        <VuiBox sx={{ height: 3, background: "linear-gradient(90deg, #0044cc, #0075ff, #00a8ff)" }} />

        <VuiBox px={3} py={3} sx={{ background: "#fafcff" }}>
          <VuiTypography sx={{ color: TC.primary, fontSize: "0.85rem", fontWeight: 700, mb: 2, pb: 1.5, borderBottom: "1px solid #e8f0fa" }}>
            📋 Annual Accounts — Records Required: {PREVIEW_VALS.companyName}
          </VuiTypography>

          <VuiTypography sx={{ color: "#1a2a4a", fontSize: "0.8rem", lineHeight: 1.8, whiteSpace: "pre-line", mb: 2.5 }}>
            {opening}
          </VuiTypography>

          <VuiBox sx={{
            background: "#ffffff",
            border: "1px solid #d8e6f7",
            borderRadius: "10px",
            overflow: "hidden",
            mb: 2.5,
            boxShadow: "0 1px 4px rgba(0,87,216,0.06)",
          }}>
            <VuiBox sx={{ background: "#f0f6ff", px: 2, py: 1, borderBottom: "1px solid #d8e6f7" }}>
              <VuiTypography sx={{ color: "#0044cc", fontWeight: 700, fontSize: "0.7rem", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                📎 Documents Required
              </VuiTypography>
            </VuiBox>
            <VuiBox px={2} py={1.5}>
              {[
                "Bank statements — all accounts (full 12 months)",
                "Sales invoices & receipts",
                "Purchase invoices & expenses",
                "Payroll records & PAYE summaries",
                "Asset purchase/disposal records",
                "Loan agreements (if applicable)",
              ].map((item, i) => (
                <VuiBox key={i} display="flex" alignItems="flex-start" gap={1.2} mb={0.8}>
                  <VuiBox sx={{
                    width: 18, height: 18, borderRadius: "4px", flexShrink: 0, mt: "1px",
                    background: "#e8f5e9", border: "1px solid #a5d6a7",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ color: "#2e7d32", fontSize: "10px", fontWeight: 700 }}>✓</span>
                  </VuiBox>
                  <VuiTypography sx={{ color: TC.midtone, fontSize: "0.75rem", lineHeight: 1.5 }}>{item}</VuiTypography>
                </VuiBox>
              ))}
            </VuiBox>
          </VuiBox>

          {form.portalUrl ? (
            <VuiBox sx={{
              background: "linear-gradient(135deg, #e8f0ff 0%, #f0f6ff 100%)",
              border: "1px solid #b8d0f7",
              borderRadius: "10px", p: 2, mb: 2.5,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
              flexWrap: "wrap",
            }}>
              <VuiBox>
                <VuiTypography sx={{ color: TC.primary, fontSize: "0.78rem", fontWeight: 700, mb: 0.3 }}>
                  🔒 Secure Upload Portal
                </VuiTypography>
                <VuiTypography sx={{ color: TC.secondary, fontSize: "0.7rem" }}>
                  Upload your documents safely — no email attachments needed
                </VuiTypography>
              </VuiBox>
              <VuiBox sx={{
                background: "#0057d8", borderRadius: "8px", px: 2, py: 1,
                cursor: "pointer", flexShrink: 0,
              }}>
                <VuiTypography sx={{ color: TC.white, fontSize: "0.72rem", fontWeight: 700 }}>
                  Upload Documents →
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          ) : (
            <VuiBox sx={{
              background: "#fff8e1", border: "1px dashed #ffca28",
              borderRadius: "8px", px: 2, py: 1.2, mb: 2.5,
            }}>
              <VuiTypography sx={{ color: "#b8860b", fontSize: "0.7rem" }}>
                💡 Add a Client Portal URL above to show an upload button here
              </VuiTypography>
            </VuiBox>
          )}

          <VuiBox sx={{ borderTop: "1px solid #e8f0fa", pt: 2 }}>
            <VuiTypography sx={{ color: TC.midtone, fontSize: "0.78rem", lineHeight: 2 }}>
              {signOff}<br />
              <strong style={{ color: TC.primary, fontSize: "0.82rem" }}>{firmName}</strong><br />
              <span style={{ color: TC.secondary, fontSize: "0.68rem" }}>Reply to: {replyTo}</span>
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {footerText && (
          <VuiBox sx={{
            background: "#f0f4fa",
            borderTop: "1px solid #d8e6f7",
            px: 3, py: 1.5,
          }}>
            <VuiTypography sx={{ color: TC.tertiary, fontSize: "0.65rem", lineHeight: 1.6, textAlign: "center" }}>
              {footerText}
            </VuiTypography>
            <VuiTypography sx={{ color: "#b0c4de", fontSize: "0.6rem", textAlign: "center", mt: 0.5 }}>
              Sent via LedgerHub · Confidential
            </VuiTypography>
          </VuiBox>
        )}
      </VuiBox>
    </VuiBox>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
const BLANK = {
  firmName:               "",
  replyToEmail:           "",
  tagline:                "",
  portalUrl:              "",
  signOffText:            "Kind regards,",
  emailFooterText:        "",
  openingParagraphMode:   "ai_generated",
  customOpeningParagraph: "Dear {contactName},\n\nWe are writing to request the financial records needed to prepare the annual accounts for {companyName} for the year ended {period}. Please provide the documents listed below as soon as possible.",
  defaultCustomNote:      "",
  // Automation
  autoRREnabled:          true,
  reminderEnabled:        true,
  reminderDaysAfter:      7,
  reminderMaxCount:       2,
  inboundEmailEnabled:    false,
};

export default function EmailConfigPage() {
  const { isSuperAdmin, isTenantAdmin, tenantId: authTenantId } = useAuthz();
  const { selectedCompanyId } = useCompany();

  const tenantId = isSuperAdmin ? (selectedCompanyId || "") : (authTenantId || "");

  const [form,    setForm]    = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [status,  setStatus]  = useState(null); // null | "saved" | "error"
  const [errMsg,  setErrMsg]  = useState("");

  // ── Scheduler status ───────────────────────────────────────────────────────
  const [rrStatus,    setRrStatus]    = useState(null);
  const [rrStatusErr, setRrStatusErr] = useState(false);

  const loadSchedulerStatus = useCallback(async () => {
    if (!tenantId) return;
    try {
      const data = await getAutoRRStatus(tenantId);
      setRrStatus(data?.schedulerStatus || null);
      setRrStatusErr(false);
    } catch (_) {
      setRrStatusErr(true);
    }
  }, [tenantId]);

  useEffect(() => { loadSchedulerStatus(); }, [loadSchedulerStatus]);

  // ── AD01 email templates ───────────────────────────────────────────────────
  const AD01_TPLS = [
    {
      key: "ad01_confirm_address",
      label: "Confirm new registered address",
      defaultSubject: "Confirming your new registered address — {{company}}",
      defaultBody: "Dear Client,\n\nWe are writing to confirm the new registered address for {{company}}.\n\nNew address: {{address}}\n\nCould you please confirm this is correct so we can proceed with filing form AD01 with Companies House?\n\nKind regards",
    },
    {
      key: "ad01_update_records",
      label: "Address change filed — notify client",
      defaultSubject: "Address change filed — {{company}}",
      defaultBody: "Dear Client,\n\nWe are pleased to confirm that the registered address change (AD01) for {{company}} has been filed with Companies House and our records have been updated.\n\nNew registered address: {{address}}\n\nPlease let us know if you have any questions.\n\nKind regards",
    },
  ];

  const [tplDraft,   setTplDraft]   = useState({});
  const [tplSel,     setTplSel]     = useState("ad01_confirm_address");
  const [tplSaving,  setTplSaving]  = useState(false);
  const [tplMsg,     setTplMsg]     = useState("");
  const [tplLoading, setTplLoading] = useState(true);

  const loadTpls = useCallback(async () => {
    if (!tenantId) return;
    setTplLoading(true);
    try {
      const saved = await getEmailTemplates(tenantId);
      const draft = {};
      AD01_TPLS.forEach((d) => {
        draft[d.key] = {
          subject: saved[d.key]?.subject ?? d.defaultSubject,
          body:    saved[d.key]?.body    ?? d.defaultBody,
        };
      });
      setTplDraft(draft);
    } catch (_) {
      const draft = {};
      AD01_TPLS.forEach((d) => { draft[d.key] = { subject: d.defaultSubject, body: d.defaultBody }; });
      setTplDraft(draft);
    } finally { setTplLoading(false); }
  }, [tenantId]);

  useEffect(() => { loadTpls(); }, [loadTpls]);

  const handleSaveTpl = async () => {
    setTplSaving(true); setTplMsg("");
    try {
      await saveEmailTemplate(tenantId, tplSel, tplDraft[tplSel]);
      setTplMsg("✓ Saved");
      setTimeout(() => setTplMsg(""), 2500);
    } catch (e) {
      setTplMsg(e?.message || "Save failed");
    } finally { setTplSaving(false); }
  };

  if (!isSuperAdmin && !isTenantAdmin) return <Redirect to="/unauthorized" />;

  const load = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getEmailConfig(tenantId);
      setForm({ ...BLANK, ...data });
    } catch (e) {
      console.error("Failed to load email config:", e);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => {
    setStatus(null);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setStatus(null);
    try {
      await saveEmailConfig(tenantId, form);
      setStatus("saved");
    } catch (e) {
      setErrMsg(e?.message || "Failed to save.");
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar customTitle="Email Configuration" />
      <VuiBox py={3}>

        {/* Page header */}
        <VuiBox mb={3} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h4" fontWeight="bold" sx={{ color: TC.primary }}>
              📨 Email Configuration
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.tertiary,  opacity: 0.7 }}>
              Customise the record request emails sent to clients
            </VuiTypography>
          </VuiBox>

          <VuiBox display="flex" alignItems="center" gap={1.5}>
            {status === "saved" && (
              <VuiBox display="flex" alignItems="center" gap={0.5}>
                <CheckCircleOutlineIcon sx={{ color: "#4caf50", fontSize: 18 }} />
                <VuiTypography variant="caption" sx={{ color: "#4caf50" }}>Saved successfully</VuiTypography>
              </VuiBox>
            )}
            {status === "error" && (
              <VuiBox display="flex" alignItems="center" gap={0.5}>
                <ErrorOutlineIcon sx={{ color: "#f44336", fontSize: 18 }} />
                <VuiTypography variant="caption" sx={{ color: "#f44336" }}>{errMsg}</VuiTypography>
              </VuiBox>
            )}
            <VuiButton
              color="info"
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={saving || loading || !tenantId}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ fontSize: "0.78rem" }}
            >
              {saving ? "Saving…" : "Save Changes"}
            </VuiButton>
          </VuiBox>
        </VuiBox>

        {!tenantId && (
          <VuiBox px={2.5} py={2} sx={{ background: "rgba(255,152,0,0.08)", border: "1px solid rgba(255,152,0,0.35)", borderRadius: "10px", mb: 3 }}>
            <VuiTypography variant="caption" sx={{ color: "#ffb74d" }}>
              ⚠️ Please select a company from the top bar to configure its email settings.
            </VuiTypography>
          </VuiBox>
        )}

        {/* ── Auto record-request scheduler status banner ─────────────── */}
        {tenantId && (() => {
          const h = rrStatusErr ? "error" : (rrStatus?.health || null);
          if (!h || h === "ok" || h === "pending") {
            // pending = normal on fresh deploy, show a subtle info pill
            if (h === "pending") return (
              <VuiBox display="flex" alignItems="center" gap={1.5} px={2.5} py={1.5} mb={3}
                sx={{ background: "rgba(0,117,255,0.07)", border: "1px solid rgba(0,117,255,0.2)", borderRadius: "12px" }}>
                <span style={{ fontSize: "1rem" }}>🕐</span>
                <VuiTypography variant="caption" sx={{ color: TC.link }}>
                  <strong>Auto record-request emails</strong> are scheduled — awaiting first run at 09:00.
                </VuiTypography>
              </VuiBox>
            );
            if (h === "ok") {
              const s = rrStatus;
              const ago = s.lastRunAt
                ? (() => {
                    const mins = Math.round((Date.now() - new Date(s.lastRunAt)) / 60000);
                    if (mins < 60) return `${mins} min ago`;
                    const hrs = Math.floor(mins / 60);
                    if (hrs < 24) return `${hrs}h ago`;
                    return `${Math.floor(hrs / 24)}d ago`;
                  })()
                : null;
              return (
                <VuiBox display="flex" alignItems="center" gap={1.5} px={2.5} py={1.5} mb={3}
                  sx={{ background: "rgba(76,175,80,0.07)", border: "1px solid rgba(76,175,80,0.25)", borderRadius: "12px" }}>
                  <CheckCircleOutlineIcon sx={{ color: "#4caf50", fontSize: 18, flexShrink: 0 }} />
                  <VuiTypography variant="caption" sx={{ color: "#81c784", lineHeight: 1.6 }}>
                    <strong style={{ color: "#a5d6a7" }}>Auto record-request emails: active</strong>
                    {ago && <> · Last run {ago}</>}
                    {s.lastRunResult && (
                      <> · <strong>{s.lastRunResult.sent}</strong> sent
                      {s.lastRunResult.failed > 0 && <>, <strong style={{ color: "#ef9a9a" }}>{s.lastRunResult.failed} failed</strong></>}
                      </>
                    )}
                    {s.nextRunAt && <> · Next: {new Date(s.nextRunAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</>}
                  </VuiTypography>
                </VuiBox>
              );
            }
            return null;
          }
          // warning or error
          const isError   = h === "error";
          const bgColor   = isError ? "rgba(244,67,54,0.08)"  : "rgba(255,152,0,0.08)";
          const border    = isError ? "rgba(244,67,54,0.35)"  : "rgba(255,152,0,0.4)";
          const textColor = isError ? "#ef9a9a"               : "#ffb74d";
          const icon      = isError ? "🔴" : "⚠️";
          const msg       = isError
            ? (rrStatus?.lastError
                ? `Scheduler error: ${rrStatus.lastError}`
                : rrStatusErr
                  ? "Could not reach the scheduler status endpoint — the service may be down."
                  : "Auto record-request scheduler is not running.")
            : "Auto record-request scheduler has not run in over 24 hours — check the server.";
          return (
            <VuiBox display="flex" alignItems="flex-start" gap={1.5} px={2.5} py={1.5} mb={3}
              sx={{ background: bgColor, border: `1px solid ${border}`, borderRadius: "12px" }}>
              <span style={{ fontSize: "1rem", marginTop: 1 }}>{icon}</span>
              <VuiBox>
                <VuiTypography variant="caption" sx={{ color: textColor, lineHeight: 1.7, display: "block" }}>
                  <strong style={{ color: isError ? "#f44336" : "#ffa726" }}>
                    {isError ? "Service alert" : "Warning"}
                  </strong>{" — "}{msg}
                </VuiTypography>
                <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem" }}>
                  Clients whose accounts period ended today will not receive automatic emails until this is resolved.
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          );
        })()}

        {!tenantId ? (
          <VuiBox display="flex" justifyContent="center" py={6}>
            <VuiTypography variant="button" sx={{ color: TC.tertiary, opacity: 0.8 }}>
              {isSuperAdmin
                ? "Please select a company to configure email settings."
                : "No company is associated with your account yet."}
            </VuiTypography>
          </VuiBox>
        ) : loading ? (
          <VuiBox display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} sx={{ color: TC.linkHover }} />
          </VuiBox>
        ) : (
          <Grid container spacing={3}>

            {/* ── Left: form ──────────────────────────────────────── */}
            <Grid item xs={12} lg={7}>
              <VuiBox display="flex" flexDirection="column" gap={3}>

                {/* Firm branding */}
                <SectionCard>
                  <SectionHeading>🏢 Firm Branding</SectionHeading>

                  <FieldRow label="Firm Name" hint="Displayed in the email header and sign-off. Defaults to your company name if blank.">
                    <TextField
                      fullWidth size="small" placeholder="e.g. Acme Accountants Ltd"
                      value={form.firmName} onChange={set("firmName")} sx={inputSx}
                    />
                  </FieldRow>

                  <FieldRow label="Tagline" hint="Short descriptor shown under the firm name in the email header.">
                    <TextField
                      fullWidth size="small" placeholder="e.g. Trusted accountants & advisors"
                      value={form.tagline} onChange={set("tagline")} sx={inputSx}
                    />
                  </FieldRow>

                  <FieldRow label="Reply-To Email" hint="Replies from clients go to this address. Leave blank to use the default sending address.">
                    <TextField
                      fullWidth size="small" type="email" placeholder="e.g. accounts@yourfirm.co.uk"
                      value={form.replyToEmail} onChange={set("replyToEmail")} sx={inputSx}
                    />
                  </FieldRow>

                  <FieldRow label="Client Portal URL" hint="Link shown in the email for clients to upload documents securely.">
                    <TextField
                      fullWidth size="small" type="url" placeholder="e.g. https://portal.yourfirm.co.uk"
                      value={form.portalUrl} onChange={set("portalUrl")} sx={inputSx}
                    />
                  </FieldRow>
                </SectionCard>

                {/* Email body */}
                <SectionCard>
                  <SectionHeading>✉️ Email Body</SectionHeading>

                  <FieldRow label="Sign-Off Text" hint="Line above the firm name at the end of the email.">
                    <TextField
                      fullWidth size="small" placeholder="e.g. Kind regards,"
                      value={form.signOffText} onChange={set("signOffText")} sx={inputSx}
                    />
                  </FieldRow>

                  <FieldRow label="Email Footer Note" hint="Small print shown at the very bottom of every email.">
                    <NativeTextarea
                      rows={2}
                      placeholder="e.g. This email was sent on behalf of your accountant. Please do not reply directly."
                      value={form.emailFooterText}
                      onChange={(e) => { setStatus(null); setForm((f) => ({ ...f, emailFooterText: e.target.value })); }}
                    />
                  </FieldRow>
                </SectionCard>

                {/* Opening paragraph */}
                <SectionCard>
                  <SectionHeading>📝 Opening Paragraph</SectionHeading>

                  <VuiBox
                    display="flex"
                    sx={{
                      background: "#f4f8ff",
                      border: "1px solid #d8e6f7",
                      borderRadius: "10px",
                      overflow: "hidden",
                      mb: 2.5,
                    }}
                  >
                    {[
                      { val: "ai_generated", label: "🤖 AI‑Generated", desc: "Automatically tailored to each client's industry, deadline & risk level" },
                      { val: "custom",       label: "✏️ Custom Template", desc: "Use your own wording with dynamic placeholders" },
                    ].map(({ val, label, desc }) => {
                      const active = form.openingParagraphMode === val;
                      return (
                        <VuiBox
                          key={val}
                          flex={1}
                          p={2}
                          sx={{
                            cursor: "pointer",
                            background: active ? "rgba(0,117,255,0.12)" : "transparent",
                            borderRight: val === "ai_generated" ? "1px solid rgba(255,255,255,0.08)" : "none",
                            transition: "background 0.15s",
                            "&:hover": { background: active ? "rgba(0,117,255,0.15)" : "rgba(255,255,255,0.03)" },
                          }}
                          onClick={() => { setStatus(null); setForm((f) => ({ ...f, openingParagraphMode: val })); }}
                        >
                          <VuiBox display="flex" alignItems="center" gap={1} mb={0.5}>
                            <VuiBox
                              sx={{
                                width: 14, height: 14, borderRadius: "50%",
                                border: `2px solid ${active ? "#0075ff" : "rgba(255,255,255,0.25)"}`,
                                background: active ? "#0075ff" : "transparent",
                                flexShrink: 0, transition: "all 0.15s",
                              }}
                            />
                            <VuiTypography variant="caption" fontWeight={active ? "bold" : "regular"} sx={{ color: TC.secondary,  fontSize: "0.8rem" }}>
                              {label}
                            </VuiTypography>
                          </VuiBox>
                          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.7rem", lineHeight: 1.4, pl: 3 }}>
                            {desc}
                          </VuiTypography>
                        </VuiBox>
                      );
                    })}
                  </VuiBox>

                  {form.openingParagraphMode === "custom" && (
                    <>
                      <FieldRow
                        label="Custom Opening Paragraph"
                        hint="Use placeholders: {contactName}, {companyName}, {period}, {deadline}"
                      >
                        <NativeTextarea
                          rows={6}
                          placeholder={"Dear {contactName},\n\nWe are writing to request..."}
                          value={form.customOpeningParagraph}
                          onChange={(e) => { setStatus(null); setForm((f) => ({ ...f, customOpeningParagraph: e.target.value })); }}
                        />
                        <VuiBox mt={1} display="flex" gap={0.8} flexWrap="wrap">
                          {["{contactName}", "{companyName}", "{period}", "{deadline}"].map((p) => (
                            <VuiBox
                              key={p}
                              px={1} py={0.3}
                              sx={{
                                background: "rgba(0,117,255,0.12)",
                                border: "1px solid rgba(0,117,255,0.25)",
                                borderRadius: "6px",
                                cursor: "pointer",
                                "&:hover": { background: "rgba(0,117,255,0.22)" },
                              }}
                              onClick={() => setForm((f) => ({ ...f, customOpeningParagraph: (f.customOpeningParagraph || "") + p }))}
                            >
                              <VuiTypography sx={{ color: TC.link, fontSize: "0.68rem", fontFamily: "monospace" }}>{p}</VuiTypography>
                            </VuiBox>
                          ))}
                          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.68rem", alignSelf: "center", ml: 0.5 }}>
                            Click to insert placeholder
                          </VuiTypography>
                        </VuiBox>
                      </FieldRow>
                    </>
                  )}

                  <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />

                  <FieldRow label="Default Custom Note" hint="Pre-filled note included in every record request. The sender can edit or clear it before sending.">
                    <NativeTextarea
                      rows={3}
                      placeholder="e.g. Please ensure bank statements cover all 12 months of the period and include all accounts."
                      value={form.defaultCustomNote}
                      onChange={(e) => { setStatus(null); setForm((f) => ({ ...f, defaultCustomNote: e.target.value })); }}
                    />
                  </FieldRow>
                </SectionCard>

                {/* Automation settings */}
                <SectionCard>
                  <SectionHeading>⚙️ Automation Settings</SectionHeading>

                  {/* Auto record request toggle */}
                  <FieldRow label="Auto Record-Request Emails" hint="When enabled, the system automatically emails clients on their Accounts Period End date.">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.autoRREnabled !== false}
                          onChange={(e) => { setStatus(null); setForm((f) => ({ ...f, autoRREnabled: e.target.checked })); }}
                          sx={{
                            "& .MuiSwitch-thumb": { backgroundColor: form.autoRREnabled !== false ? "#0075ff" : undefined },
                            "& .MuiSwitch-track": { backgroundColor: form.autoRREnabled !== false ? "rgba(0,117,255,0.4)" : undefined },
                          }}
                        />
                      }
                      label={
                        <VuiTypography variant="caption" sx={{ color: form.autoRREnabled !== false ? "#90caf9" : "rgba(255,255,255,0.4)", fontSize: "0.8rem" }}>
                          {form.autoRREnabled !== false ? "Enabled — emails sent automatically on period end date" : "Disabled — auto emails will not be sent"}
                        </VuiTypography>
                      }
                    />
                  </FieldRow>

                  <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />

                  {/* Smart reminders */}
                  <FieldRow label="Smart Reminders" hint="Automatically follow up if the client hasn't responded after a set number of days.">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.reminderEnabled !== false}
                          onChange={(e) => { setStatus(null); setForm((f) => ({ ...f, reminderEnabled: e.target.checked })); }}
                          sx={{
                            "& .MuiSwitch-thumb": { backgroundColor: form.reminderEnabled !== false ? "#0075ff" : undefined },
                            "& .MuiSwitch-track": { backgroundColor: form.reminderEnabled !== false ? "rgba(0,117,255,0.4)" : undefined },
                          }}
                        />
                      }
                      label={
                        <VuiTypography variant="caption" sx={{ color: form.reminderEnabled !== false ? "#90caf9" : "rgba(255,255,255,0.4)", fontSize: "0.8rem" }}>
                          {form.reminderEnabled !== false ? "Enabled — reminder emails sent automatically" : "Disabled"}
                        </VuiTypography>
                      }
                    />
                  </FieldRow>

                  {form.reminderEnabled !== false && (
                    <VuiBox
                      mt={1.5}
                      p={2}
                      sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: "10px" }}
                    >
                      <VuiBox display="flex" gap={3} flexWrap="wrap">
                        <VuiBox flex={1} minWidth={120}>
                          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.72rem", display: "block", mb: 0.8 }}>
                            Send reminder after
                          </VuiTypography>
                          <VuiBox display="flex" alignItems="center" gap={1}>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 1, max: 30 }}
                              value={form.reminderDaysAfter ?? 7}
                              onChange={(e) => { setStatus(null); setForm((f) => ({ ...f, reminderDaysAfter: Math.max(1, Math.min(30, Number(e.target.value) || 7)) })); }}
                              sx={{ ...inputSx, width: 80 }}
                            />
                            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.78rem" }}>days</VuiTypography>
                          </VuiBox>
                        </VuiBox>
                        <VuiBox flex={1} minWidth={120}>
                          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.72rem", display: "block", mb: 0.8 }}>
                            Maximum reminders
                          </VuiTypography>
                          <VuiBox display="flex" alignItems="center" gap={1}>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 1, max: 5 }}
                              value={form.reminderMaxCount ?? 2}
                              onChange={(e) => { setStatus(null); setForm((f) => ({ ...f, reminderMaxCount: Math.max(1, Math.min(5, Number(e.target.value) || 2)) })); }}
                              sx={{ ...inputSx, width: 80 }}
                            />
                            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.78rem" }}>emails</VuiTypography>
                          </VuiBox>
                        </VuiBox>
                      </VuiBox>
                      <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.68rem", mt: 1.5, display: "block" }}>
                        e.g. with 7 days / 2 max: 1st reminder sent 7 days after initial email, 2nd reminder 7 days after that.
                      </VuiTypography>
                    </VuiBox>
                  )}

                  <Divider sx={{ my: 2, bordercolor: "#7a94b0" }} />

                  {/* Email ingestion */}
                  <FieldRow label="Email Ingestion (Inbound Replies)" hint="Parse client reply emails and automatically link them to their profile and record requests.">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.inboundEmailEnabled === true}
                          onChange={(e) => { setStatus(null); setForm((f) => ({ ...f, inboundEmailEnabled: e.target.checked })); }}
                          sx={{
                            "& .MuiSwitch-thumb": { backgroundColor: form.inboundEmailEnabled ? "#0075ff" : undefined },
                            "& .MuiSwitch-track": { backgroundColor: form.inboundEmailEnabled ? "rgba(0,117,255,0.4)" : undefined },
                          }}
                        />
                      }
                      label={
                        <VuiTypography variant="caption" sx={{ color: form.inboundEmailEnabled ? "#90caf9" : "rgba(255,255,255,0.4)", fontSize: "0.8rem" }}>
                          {form.inboundEmailEnabled ? "Enabled" : "Disabled"}
                        </VuiTypography>
                      }
                    />
                    {form.inboundEmailEnabled && (
                      <VuiBox mt={1.5} p={2} sx={{ background: "rgba(0,117,255,0.04)", border: "1px solid rgba(0,117,255,0.15)", borderRadius: "10px" }}>
                        <VuiTypography variant="caption" sx={{ color: TC.link, fontSize: "0.73rem", fontWeight: "bold", display: "block", mb: 1 }}>
                          📡 SendGrid Inbound Parse Setup
                        </VuiTypography>
                        {[
                          "Go to SendGrid → Settings → Inbound Parse",
                          "Add your receiving domain (e.g. reply.yourfirm.co.uk) and verify DNS",
                          `Set the destination URL to: ${(process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "https://your-api.com/api/v1")}/inbound-email`,
                          "Add EMAIL_INGEST_SECRET to your .env for webhook signature verification (optional)",
                          "Client reply emails are automatically matched by sender address and linked to their profile",
                        ].map((step, i) => (
                          <VuiTypography key={i} variant="caption" sx={{ color: TC.secondary, fontSize: "0.7rem", display: "block", lineHeight: 1.7 }}>
                            {i + 1}. {step}
                          </VuiTypography>
                        ))}
                      </VuiBox>
                    )}
                  </FieldRow>
                </SectionCard>

                {/* ── AD01 Email Templates ────────────────────────── */}
                <SectionCard>
                  <SectionHeading>📬 AD01 Email Templates</SectionHeading>
                  <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.75rem", mb: 2, display: "block" }}>
                    Pre-filled subject and body for address change task emails. Use <code style={{ background: "#f0f5ff", borderRadius: 3, padding: "1px 5px" }}>{"{{company}}"}</code> and <code style={{ background: "#f0f5ff", borderRadius: 3, padding: "1px 5px" }}>{"{{address}}"}</code> as placeholders.
                  </VuiTypography>

                  {/* Template picker tabs */}
                  <VuiBox display="flex" gap={1} mb={2.5} sx={{ flexWrap: "wrap" }}>
                    {AD01_TPLS.map((d) => (
                      <VuiButton key={d.key} size="small"
                        variant={tplSel === d.key ? "contained" : "outlined"} color="info"
                        onClick={() => { setTplSel(d.key); setTplMsg(""); }}
                        sx={{ fontSize: "0.72rem", px: 1.5, py: 0.6 }}>
                        {d.label}
                      </VuiButton>
                    ))}
                  </VuiBox>

                  {tplLoading ? (
                    <VuiBox display="flex" justifyContent="center" py={3}>
                      <CircularProgress size={22} sx={{ color: TC.linkHover }} />
                    </VuiBox>
                  ) : (
                    <>
                      <FieldRow label="Subject">
                        <TextField fullWidth size="small"
                          value={tplDraft[tplSel]?.subject || ""}
                          onChange={(e) => setTplDraft((p) => ({ ...p, [tplSel]: { ...p[tplSel], subject: e.target.value } }))}
                          sx={{ ...inputSx, "& input": { width: "100% !important" } }}
                        />
                      </FieldRow>

                      <FieldRow label="Message Body">
                        <NativeTextarea rows={8}
                          value={tplDraft[tplSel]?.body || ""}
                          onChange={(e) => setTplDraft((p) => ({ ...p, [tplSel]: { ...p[tplSel], body: e.target.value } }))}
                          placeholder="Email body…"
                        />
                      </FieldRow>

                      <VuiBox display="flex" alignItems="center" gap={2} mt={1}>
                        <VuiButton size="small" color="info" variant="contained"
                          disabled={tplSaving || !tenantId}
                          onClick={handleSaveTpl}
                          startIcon={tplSaving ? <CircularProgress size={12} color="inherit" /> : null}
                          sx={{ fontSize: "0.78rem", px: 2.5 }}>
                          {tplSaving ? "Saving…" : "Save Template"}
                        </VuiButton>
                        <VuiButton size="small" variant="outlined" color="secondary"
                          onClick={() => {
                            const def = AD01_TPLS.find((d) => d.key === tplSel);
                            if (def) setTplDraft((p) => ({ ...p, [tplSel]: { subject: def.defaultSubject, body: def.defaultBody } }));
                          }}
                          sx={{ fontSize: "0.78rem", px: 2 }}>
                          Reset
                        </VuiButton>
                        {tplMsg && (
                          <VuiTypography sx={{ fontSize: "0.78rem", color: tplMsg.startsWith("✓") ? "#4caf50" : "#f44336" }}>
                            {tplMsg}
                          </VuiTypography>
                        )}
                      </VuiBox>
                    </>
                  )}
                </SectionCard>

              </VuiBox>
            </Grid>

            {/* ── Right: live preview ──────────────────────────── */}
            <Grid item xs={12} lg={5}>
              <VuiBox position="sticky" top={24}>
                <VuiBox mb={1.5} display="flex" alignItems="center" gap={1}>
                  <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.tertiary,  textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.68rem", opacity: 0.7 }}>
                    Live Preview
                  </VuiTypography>
                  <VuiBox px={1} py={0.2} sx={{ background: "rgba(0,117,255,0.12)", border: "1px solid rgba(0,117,255,0.2)", borderRadius: "6px" }}>
                    <VuiTypography sx={{ color: TC.link, fontSize: "0.62rem" }}>Sample client: {PREVIEW_VALS.companyName}</VuiTypography>
                  </VuiBox>
                </VuiBox>
                <EmailPreview form={form} />
                <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.65rem", mt: 1, display: "block" }}>
                  Preview uses sample values. Actual emails are personalised per client.
                </VuiTypography>
              </VuiBox>
            </Grid>

          </Grid>
        )}
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

